import Phaser from "phaser";

export type OnboardingPointerDirection = "up" | "down" | "left" | "right";

export type OnboardingPointerPresentation = {
  visible: boolean;
  x: number;
  y: number;
  direction: OnboardingPointerDirection;
  reducedMotion: boolean;
};

export type OnboardingPointerSnapshot = {
  visible: boolean;
  direction: OnboardingPointerDirection;
  x: number;
  y: number;
  angle: number;
  renderWidth: number;
  renderHeight: number;
};

export type OnboardingPointerCallbacks = {
  onReady?(): void;
};

const DIRECTION_ANGLE: Record<OnboardingPointerDirection, number> = {
  down: 0,
  left: 90,
  up: 180,
  right: -90,
};

const DIRECTION_VECTOR: Record<OnboardingPointerDirection, Phaser.Math.Vector2> = {
  down: new Phaser.Math.Vector2(0, 1),
  left: new Phaser.Math.Vector2(-1, 0),
  up: new Phaser.Math.Vector2(0, -1),
  right: new Phaser.Math.Vector2(1, 0),
};

export class OnboardingPointerScene extends Phaser.Scene {
  readonly #callbacks: OnboardingPointerCallbacks;
  readonly #renderScale: number;
  #presentation: OnboardingPointerPresentation;
  #width = 0;
  #height = 0;
  #pointer!: Phaser.GameObjects.Container;
  #aura!: Phaser.GameObjects.Arc;
  #trail: Phaser.GameObjects.Polygon[] = [];
  #motionTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    callbacks: OnboardingPointerCallbacks,
    presentation: OnboardingPointerPresentation,
    renderScale: number,
  ) {
    super({ key: "OnboardingPointer" });
    this.#callbacks = callbacks;
    this.#presentation = presentation;
    this.#renderScale = renderScale;
  }

  create(): void {
    this.cameras.main.setBackgroundColor("rgba(0,0,0,0)");
    this.cameras.main.transparent = true;

    const outerGlow = this.add.graphics();
    outerGlow.lineStyle(12, 0x0b403f, 0.56);
    outerGlow.beginPath();
    outerGlow.moveTo(0, -24);
    outerGlow.lineTo(0, 3);
    outerGlow.strokePath();
    outerGlow.fillStyle(0x0b403f, 0.56);
    outerGlow.fillTriangle(-16, -1, 0, 21, 16, -1);

    const arrow = this.add.graphics();
    arrow.lineStyle(7, 0xffd98d, 1);
    arrow.beginPath();
    arrow.moveTo(0, -24);
    arrow.lineTo(0, 2);
    arrow.strokePath();
    arrow.fillStyle(0xffd98d, 1);
    arrow.fillTriangle(-13, 0, 0, 18, 13, 0);
    arrow.lineStyle(2, 0x7ff7e7, 0.95);
    arrow.beginPath();
    arrow.moveTo(0, -22);
    arrow.lineTo(0, 5);
    arrow.strokePath();
    arrow.fillStyle(0x7ff7e7, 0.9);
    arrow.fillTriangle(-5, 1, 0, 10, 5, 1);

    this.#aura = this.add
      .circle(0, 15, 18, 0x73f7e7, 0.08)
      .setStrokeStyle(2, 0xffdf9a, 0.62)
      .setBlendMode(Phaser.BlendModes.ADD);

    this.#trail = [0, 1, 2].map((index) =>
      this.add
        .polygon(0, -35 - index * 11, [0, -4, 3, 0, 0, 4, -3, 0], index === 1 ? 0x70f3e3 : 0xffd58a, 0.8 - index * 0.18)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setScale(1 - index * 0.14),
    );

    this.#pointer = this.add.container(0, 0, [outerGlow, this.#aura, arrow, ...this.#trail]);
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#applyPresentation();
    this.#callbacks.onReady?.();
  }

  setPresentation(presentation: OnboardingPointerPresentation): void {
    const changed =
      presentation.visible !== this.#presentation.visible ||
      presentation.direction !== this.#presentation.direction ||
      presentation.reducedMotion !== this.#presentation.reducedMotion ||
      Math.abs(presentation.x - this.#presentation.x) > 0.5 ||
      Math.abs(presentation.y - this.#presentation.y) > 0.5;
    if (!changed) return;
    this.#presentation = presentation;
    if (this.sys.isActive()) this.#applyPresentation();
  }

  debugSnapshot(): OnboardingPointerSnapshot {
    return {
      visible: this.#pointer.visible,
      direction: this.#presentation.direction,
      x: this.#pointer.x,
      y: this.#pointer.y,
      angle: this.#pointer.angle,
      renderWidth: this.#width,
      renderHeight: this.#height,
    };
  }

  #layout(size: Phaser.Structs.Size): void {
    const physicalWidth = Math.round(size.width);
    const physicalHeight = Math.round(size.height);
    const width = Math.round(physicalWidth / this.#renderScale);
    const height = Math.round(physicalHeight / this.#renderScale);
    if (width <= 0 || height <= 0) return;
    this.#width = width;
    this.#height = height;
    this.cameras.main
      .setViewport(0, 0, physicalWidth, physicalHeight)
      .setZoom(this.#renderScale)
      .centerOn(width / 2, height / 2);
    if (this.#pointer !== undefined) this.#applyPresentation();
  }

  #applyPresentation(): void {
    this.#motionTweens.forEach((tween) => tween.stop());
    this.#motionTweens = [];
    this.tweens.killTweensOf([this.#pointer, this.#aura, ...this.#trail]);

    const x = Phaser.Math.Clamp(this.#presentation.x, 28, Math.max(28, this.#width - 28));
    const y = Phaser.Math.Clamp(this.#presentation.y, 28, Math.max(28, this.#height - 28));
    this.#pointer
      .setPosition(x, y)
      .setAngle(DIRECTION_ANGLE[this.#presentation.direction])
      .setScale(1)
      .setAlpha(1)
      .setVisible(this.#presentation.visible);
    this.#aura.setScale(1).setAlpha(0.72);
    this.#trail.forEach((shard, index) => shard.setAlpha(0.72 - index * 0.16).setScale(1 - index * 0.14));

    if (!this.#presentation.visible || this.#presentation.reducedMotion) return;
    const vector = DIRECTION_VECTOR[this.#presentation.direction];
    this.#motionTweens.push(
      this.tweens.add({
        targets: this.#pointer,
        x: x + vector.x * 11,
        y: y + vector.y * 11,
        duration: 520,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: -1,
      }),
      this.tweens.add({
        targets: this.#aura,
        alpha: { from: 0.42, to: 0.92 },
        scale: { from: 0.78, to: 1.16 },
        duration: 620,
        ease: "Sine.easeInOut",
        yoyo: true,
        repeat: -1,
      }),
    );
    this.#trail.forEach((shard, index) => {
      this.#motionTweens.push(
        this.tweens.add({
          targets: shard,
          alpha: { from: 0.2, to: 0.84 - index * 0.12 },
          scale: { from: 0.62 - index * 0.08, to: 1.04 - index * 0.1 },
          duration: 540,
          delay: index * 95,
          ease: "Sine.easeInOut",
          yoyo: true,
          repeat: -1,
        }),
      );
    });
  }
}
